import { useCallback, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import {
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Flex,
  Text,
  Center,
} from '@chakra-ui/react';
import { PageWithTradeMark } from '@/components/shared/onboarding-page';

import { isInvalidField } from '@/lib/util';
import { renderErrors } from '@/ui/util';
import { PasswordHelper } from '@/ui/PasswordStrengthMeter';
import { updateMePasswordReset } from '@/store/mePasswordReset/actions';
import { useSchemaForUpdate } from '@/formConfigs/passwordSchema';
import { CheckCircle, Mail01, MinusCircle } from '@kula-ai/icons-core';

export function PasswordForm({ token = null, onComplete = () => {} }) {
  const { current } = useRef({});
  const dispatch = useDispatch();
  const validationSchema = useSchemaForUpdate(current);
  const [showHelper, setShowHelper] = useState(false);
  const onSubmit = useCallback(
    async (values, actions) => {
      try {
        const { password } = values;
        const reset_password_token = token;
        const user = { reset_password_token, password };
        await dispatch(updateMePasswordReset({ user })).unwrap();
        onComplete();
      } catch {
        // noop
      } finally {
        actions.setSubmitting(false);
      }
    },
    [token, onComplete, dispatch]
  );

  return (
    <PageWithTradeMark>
      <Flex width="380px" px="20px" flexDirection="column">
        <Center
          width="56px"
          height="56px"
          bg="var(--kula-dark-primary-50, #E0EAFF)"
          borderRadius="full"
          mt="16px"
        >
          <Icon
            as={Mail01}
            width="28px"
            height="28px"
            color="var(--kula-dark-primary-700, #444CE7)"
          />
        </Center>
        <Text as="h1" fontSize="18px" fontWeight="semiBold" mt="16px">
          Create your password
        </Text>
        <Text fontSize="14px" fontWeight={400}>
          Make sure to pick something unique!
        </Text>
        <Formik
          initialValues={{}}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {(formik) => (
            <Form noValidate style={{ marginTop: '20px' }}>
              <Stack spacing={4}>
                <Field name="password">
                  {({ field, form, meta }) => (
                    <FormControl isInvalid={isInvalidField(form, meta)}>
                      <FormLabel
                        htmlFor={field.name}
                        fontSize="14px"
                        fontWeight="semiBold"
                      >
                        Password
                      </FormLabel>
                      <InputGroup>
                        <Input
                          {...field}
                          id={field.name}
                          value={field.value || ''}
                          type="password"
                          onFocus={() => setShowHelper(true)}
                        />
                        <InputRightElement>
                          {!meta.touched ? null : (
                            <Icon
                              as={meta.error ? MinusCircle : CheckCircle}
                              color={meta.error ? 'red.500' : 'green.500'}
                              boxSize="20px"
                            />
                          )}
                        </InputRightElement>
                      </InputGroup>
                      <FormErrorMessage
                        flexDir="column"
                        alignItems="flex-start"
                        w="calc(100% - 128px)"
                      >
                        {meta.error === 'password-complexity'
                          ? ''
                          : renderErrors('Password', meta.error)}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
                {showHelper && (
                  <PasswordHelper
                    top="10vh"
                    right={{
                      base: '28vw',
                      xl: '35vw',
                      '2xl': '41vw',
                    }}
                    value={formik.values.password}
                  />
                )}
                <Field name="password_confirmation">
                  {({ field, form, meta }) => (
                    <FormControl isInvalid={isInvalidField(form, meta)}>
                      <FormLabel
                        htmlFor={field.name}
                        fontSize="14px"
                        fontWeight="semiBold"
                      >
                        Confirm Password
                      </FormLabel>
                      <Input
                        {...field}
                        id={field.name}
                        value={field.value || ''}
                        type="password"
                      />
                      <FormErrorMessage
                        flexDir="column"
                        alignItems="flex-start"
                      >
                        {renderErrors('', meta.error)}
                      </FormErrorMessage>
                    </FormControl>
                  )}
                </Field>
              </Stack>
              <Stack py={6}>
                <Button
                  type="submit"
                  variant="primary"
                  isLoading={formik.isSubmitting}
                >
                  Save password
                </Button>
              </Stack>
            </Form>
          )}
        </Formik>
      </Flex>
    </PageWithTradeMark>
  );
}
